/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MatDialogRef} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG, INVALID_CHARACTERS, VEHICLE_TYPES} from '../../../../common/constants';
import {StateService} from '../../../../core/state/state.service';
import {User} from '../../../../models/user.model';
import {SegmentService} from '../../../../services/segment.service';

@Component({
    selector: 'app-new-segment-popup',
    templateUrl: './add-segment-popup.component.html',
    styleUrls: ['./add-segment-popup.component.scss'],
    standalone: false
})
export class AddSegmentPopupComponent implements OnInit {

  segmentForm: FormGroup;
  vehicleTypes = VEHICLE_TYPES;
  showSpinner = false;
  errorMessage = '';
  user: User;

  constructor(private readonly segmentService: SegmentService,
              private readonly userService: StateService,
              public dialogRef: MatDialogRef<AddSegmentPopupComponent>,
              private readonly snackbar: FabMessageService) {
  }

  ngOnInit(): void {
    this.user = this.userService.user;
    this.segmentForm = new FormGroup({
      segment: new FormControl(null, Validators.required),
      vehicleType: new FormControl(this.vehicleTypes[0], Validators.required)
    });
  }

  /**
   * Creates a Segment object from user input and call backend endpoint to generate
   * segments. Level 1 is user-inputted, level 3 is auto-generated EV/ICE breakdown.
   */
  onSubmit(): void {
    if (this.validateSegmentInput()) {
      this.showSpinner = true;
      const segment = {
        segment: this.segmentForm.get('segment').value,
        vehType: this.segmentForm.get('vehicleType').value,
        userId: this.user.gmid
      };
      this.segmentService.createSegment(segment).subscribe({
        error: err => {
          const errorMessage = err.error ? err.error.message : err;
          this.snackbar.displayError(errorMessage, DISMISS_MSG);
          this.showSpinner = false;
        },
        complete: () => {
          this.snackbar.displaySuccess('Successfully created segment', DISMISS_MSG);
          this.onCancel();
        }
      });
    } else {
      this.snackbar.displayError(this.errorMessage, DISMISS_MSG);
    }
  }

  /**
   * Validates segment input for invalid characters and inputs over 21 characters.
   */
  validateSegmentInput(): boolean {
    // Database has a varchar of 25 - we need to account for the '-ICE' prefix
    if (this.segmentForm.get('segment').value.length > 21) {
      this.errorMessage = 'Segment cannot exceed 21 characters';
      return false;
    } else if (INVALID_CHARACTERS.test(this.segmentForm.get('segment').value)) {
      this.errorMessage = 'Segment contains invalid characters';
      return false;
    }
    return true;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

}
